import type { ChangeItem, RunState } from "./types";

/**
 * STATUS META — the single, EXHAUSTIVE map from a machine state to human language.
 *
 * Raw enums never reach the operator. `status.replace(/_/g, " ")` is deleted: it printed
 * "blocked for human" as if it were a mood, and it made every unmapped future state look
 * benign by default.
 *
 * The record is typed with `satisfies Record<PillStatus, StatusMeta>` and has NO index
 * signature and NO fallback. If a new RunState is added to lib/types.ts and not mapped here,
 * THE BUILD FAILS. That is deliberate: a governance product must never let
 * "blocked_for_human" (you cannot export) silently degrade into looking like "needs_review"
 * (keep going).
 *
 * Verdict is carried by GLYPH + TEXT + COLOUR together, never by colour alone (WCAG 1.4.1).
 * On the one screen where approved-versus-rejected IS the entire job, hue is not enough.
 */

export type StatusTone = "neutral" | "progress" | "success" | "caution" | "danger";

/** The lucide icon is chosen in the component; the meta names the SHAPE so it stays stable. */
export type StatusGlyph = "dot" | "clock" | "spinner" | "check" | "alert" | "cross" | "lock";

export type StatusMeta = {
  /** What the operator reads on the pill. */
  label: string;
  /** One line: what this state MEANS. */
  meaning: string;
  /** One line: what to do next. */
  next: string;
  tone: StatusTone;
  glyph: StatusGlyph;
};

export type PillStatus = RunState | ChangeItem["verdict"];

export const STATUS_META = {
  draft: {
    label: "Draft",
    meaning: "The document exists but no review has started.",
    next: "Open the editor and prepare the copy.",
    tone: "neutral",
    glyph: "dot"
  },
  queued: {
    label: "Queued",
    meaning: "The run is waiting for the compiler.",
    next: "No action needed yet.",
    tone: "progress",
    glyph: "clock"
  },
  running: {
    label: "Running",
    meaning: "The compiler is working on this run.",
    next: "Keep this page open.",
    tone: "progress",
    glyph: "spinner"
  },
  needs_review: {
    label: "Needs review",
    meaning: "Proposed edits are waiting for a human decision.",
    next: "Approve or reject every proposed edit.",
    tone: "caution",
    glyph: "alert"
  },
  approved: {
    label: "Approved",
    meaning: "A human recorded an approval for this run.",
    next: "Package the final copy.",
    tone: "success",
    glyph: "check"
  },
  blocked: {
    label: "Blocked",
    meaning: "This run cannot proceed.",
    next: "Resolve the blocker, then run final QA again.",
    tone: "danger",
    glyph: "cross"
  },
  blocked_for_human: {
    label: "Blocked — human review",
    meaning: "Export is blocked. QA could not clear this run on its own.",
    next: "Read the blocked checks, fix them, then run final QA again.",
    tone: "danger",
    glyph: "cross"
  },
  pending: {
    label: "Pending",
    meaning: "This edit has no decision yet.",
    next: "Approve or reject it.",
    tone: "caution",
    glyph: "alert"
  },
  rejected: {
    label: "Rejected",
    meaning: "This edit is excluded from the final copy.",
    next: "Flip the decision if that was wrong.",
    tone: "danger",
    glyph: "cross"
  }
} satisfies Record<PillStatus, StatusMeta>;

/** Total, by construction — the `satisfies` above guarantees the key exists. */
export function statusMeta(status: PillStatus): StatusMeta {
  return STATUS_META[status];
}

export function statusLabel(status: PillStatus) {
  return STATUS_META[status].label;
}

/**
 * Backend strings that are NOT a status: a terminalReason, a purpose key, a run id.
 * They still need their underscores softened, but they get no promise of meaning.
 */
export function humanizeToken(value: string) {
  return value.replace(/_/g, " ");
}
